import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { makeStyles } from "@material-ui/core/styles";
import {
  CheckIcon,
  XIcon,
  DeleteIcon,
} from "../../../../components/common/iconography";
import { green, red } from "@material-ui/core/colors";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  iconContainer: {
    height: 38,
    width: 38,
  },
  valid: {
    backgroundColor: green[300],
  },
  inValid: {
    backgroundColor: red[300],
  },
  container: {
    padding: theme.spacing(2),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  primaryColor: {
    color: theme.palette.primary.main,
  },
}));

const ConfirmDialog = ({ open, onClose, onConfirm, saving, barcode }) => {
  const styles = useStyles();
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <Typography>
          Are you sure you want to delete the seal with barcode{" "}
          <span className={styles.primaryColor}>{barcode}</span> ?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          className={styles.deleteButton}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SealsList = ({
  close,
  handleDelete,
  onPressAddSeal,
  saving,
  sealsList,
}) => {
  const styles = useStyles();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedSeal, setSelectedSeal] = useState(null);

  useEffect(() => {
    if (!saving) {
      setConfirmDialogOpen(false);
    }
  }, [saving]);

  const onDeleteSeal = (seal) => {
    setSelectedSeal(seal);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    handleDelete(selectedSeal.id);
  };

  const handleCancelDelete = () => {
    setSelectedSeal(null);
    setConfirmDialogOpen(false);
  };

  const getAvatarClass = (validated) => {
    return clsx(styles.iconContainer, {
      [styles.valid]: validated,
      [styles.inValid]: !validated,
    });
  };

  const getListItem = (id, validated, barcode, description, idx) => {
    return (
      <div key={id}>
        <ListItem>
          <ListItemAvatar>
            <Avatar className={getAvatarClass(validated)}>
              {validated ? <CheckIcon fontSize="small" /> : <XIcon />}
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={barcode} secondary={description} />
          <ListItemSecondaryAction>
            <IconButton onClick={() => onDeleteSeal({ id, barcode })}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        {idx < sealsList.length - 1 ? <Divider /> : null}
      </div>
    );
  };

  return (
    <>
      <Dialog open={true} fullWidth={true}>
        <Box
          component="span"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          m={2}
        >
          <Typography variant="h6">Create a Seal</Typography>
          <CancelOutlinedIcon fontSize="large" onClick={close} />
        </Box>
        <DialogContent className={styles.container}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <List>
                {sealsList.map(({ id, validated, barcode, description }, idx) =>
                  getListItem(id, validated, barcode, description, idx),
                )}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Close
          </Button>
          <Button color="primary" onClick={onPressAddSeal} disabled={saving}>
            Add Seal
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        saving={saving}
        barcode={selectedSeal?.barcode}
      />
    </>
  );
};
